/* eslint-disable no-unused-vars */
export default class ProductGrid extends window.HTMLElement {
  private isLoading = false;
  private currentPage = 1;
  private totalPages = 1;
  private hasNextPage = false;
  private loadMoreHandler: ((e: MouseEvent) => void) | null = null;

  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    super();
    // Initialize pagination data
    this.initializePaginationData();

    // Initialize product cards layout
    this.initProductCardsLayout();

    // Setup load more button
    this.setupLoadMoreButton();

    // Delay removal of loading until layout is fully ready
    requestAnimationFrame(() => {
      this.removeAttribute('data-loading');
      this.applyPriceSortIfNeeded();
      this.checkGridAlignment();
    });

    // Handle view change
    this.querySelectorAll('input[name="view"]').forEach((input) => {
      input.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        const view = target.id === 'view-horizontal' ? 'horizontal' : 'vertical';
        this.setAttribute('data-view', view);
        localStorage.setItem('productView', view);
        this.updateProductCardsLayout(view);
      });
    });

    // Setup resize observer to recalculate alignment on container resize
    this.setupResizeObserver();
  }

  initProductCardsLayout() {
    // Force vertical
    // const savedView = localStorage.getItem('productView');
    // const view = savedView || 'vertical';
    const view = 'vertical';

    const viewElement = this.querySelector(`#view-${view}`);
    if (viewElement instanceof HTMLInputElement) {
      viewElement.checked = true;
    }

    this.setAttribute('data-view', view);
    this.updateProductCardsLayout(view);
  }

  resetProductCardsLayout() {
    const view = this.getAttribute('data-view');
    this.updateProductCardsLayout(view || 'vertical');
  }

  // connectedCallback() {
  // }

  public initializePaginationData() {
    const container = this.querySelector('#product-grid-container') as HTMLElement;
    if (!container) return;

    this.currentPage = parseInt(container.dataset.currentPage || '1');
    this.totalPages = parseInt(container.dataset.totalPages || '1');
    this.hasNextPage = container.dataset.hasNextPage === '1';
  }

  public setupLoadMoreButton() {
    const button = this.querySelector('#load-more-button') as HTMLButtonElement;

    // If no button exists, that's fine - it means there are no more pages
    if (!button) {
      return;
    }

    // If we don't have a next page, remove the button
    if (!this.hasNextPage) {
      button.parentElement?.remove();
      return;
    }

    // Remove any existing event listeners to prevent duplicates
    if (this.loadMoreHandler) {
      button.removeEventListener('click', this.loadMoreHandler);
    }

    // Create and store the event handler
    this.loadMoreHandler = (e: MouseEvent) => {
      e.preventDefault();

      this.loadMoreProducts();
    };

    // Add the event listener
    button.addEventListener('click', this.loadMoreHandler);
  }

  private async loadMoreProducts() {
    if (this.isLoading || !this.hasNextPage) return;

    this.isLoading = true;
    const button = this.querySelector('#load-more-button') as HTMLButtonElement;
    const loadingText = button?.querySelector('[data-load-more-loading]');
    const normalText = button?.querySelector('[data-load-more-text]');

    try {
      // Show loading state
      if (button) {
        button.disabled = true;
        normalText?.classList.add('hidden');
        loadingText?.classList.remove('hidden');
      }

      const nextPage = this.currentPage + 1;
      const currentUrl = new URL(window.location.href);
      const searchParams = new URLSearchParams(currentUrl.search);

      // Update page parameter and add section_id for AJAX request
      searchParams.set('page', nextPage.toString());
      searchParams.set('section_id', this.dataset.id || '');
      const ajaxUrl = `${currentUrl.pathname}?${searchParams.toString()}`;

      const response = await fetch(ajaxUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      await this.appendProducts(html);

      // Update pagination state
      this.currentPage = nextPage;
      this.hasNextPage = nextPage < this.totalPages;

      // Update data attributes on container
      const container = this.querySelector('#product-grid-container') as HTMLElement;
      if (container) {
        container.dataset.currentPage = this.currentPage.toString();
        container.dataset.hasNextPage = this.hasNextPage ? '1' : '0';
      }

      // Remove button if no more pages
      if (!this.hasNextPage && button) {
        button.parentElement?.remove();
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      this.showError();
    } finally {
      this.isLoading = false;
      if (button) {
        button.disabled = false;
        normalText?.classList.remove('hidden');
        loadingText?.classList.add('hidden');
      }
    }
  }

  private async appendProducts(html: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newProducts = doc.querySelectorAll('#product-list > li');
    const productList = this.querySelector('#product-list');

    if (!productList || !newProducts.length) return;

    // Get current view for proper styling
    const currentView = this.getAttribute('data-view') || 'vertical';

    // Append new products with proper classes
    newProducts.forEach((product) => {
      const productElement = product.cloneNode(true) as HTMLElement;

      // Apply current layout to new products
      const productCard = productElement.querySelector('article[data-layout]');
      if (productCard instanceof HTMLElement) {
        productCard.setAttribute('data-layout', currentView);
      }

      productList.appendChild(productElement);

      this.initializeTrustpilot(productElement);
    });

    // Re-apply client-side sorting after new products are appended
    this.applyPriceSortIfNeeded();

    // Recalculate grid alignment after new products are added
    this.checkGridAlignment();
  }

  private showError() {
    const button = this.querySelector('#load-more-button');
    if (button?.parentElement) {
      button.parentElement.innerHTML = `
        <div class="flex items-center gap-2 text-red-600">
          <span class="text-sm">Failed to load more products. <button class="underline font-medium" onclick="location.reload()">Refresh page</button></span>
        </div>
      `;
    }
  }

  private updateProductCardsLayout(view: string) {
    const productCards = this.querySelectorAll('article[data-layout]');
    productCards.forEach((card) => {
      if (card instanceof HTMLElement) {
        card.setAttribute('data-layout', view);
      }

      this.initializeTrustpilot(card);
    });
  }

  private initializeTrustpilot(element: Element) {
    const trustpilotWidget = element.querySelector('.trustpilot-widget');
    if (
      trustpilotWidget instanceof HTMLElement &&
      'Trustpilot' in window &&
      window.Trustpilot &&
      typeof window.Trustpilot.loadFromElement === 'function'
    ) {
      window.Trustpilot.loadFromElement(trustpilotWidget);
    }
  }

  private getSortByParam(): string | null {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('sort_by');
    } catch {
      return null;
    }
  }

  private applyPriceSortIfNeeded() {
    const sortBy = this.getSortByParam();
    if (!sortBy) return;

    const isAsc = sortBy === 'price-ascending';
    const isDesc = sortBy === 'price-descending';
    if (!isAsc && !isDesc) return;

    const productList = this.querySelector('#product-list');
    if (!productList) return;

    const listItems = Array.from(productList.children) as HTMLElement[];

    // Extract only product <li> elements (those that contain an article with data-product-id)
    const productItems: Array<{
      li: HTMLElement;
      priceCents: number;
      hasAvailable: boolean;
      originalOrder: number;
      stableIndex: number;
    }> = [];

    let index = 0;
    for (const li of listItems) {
      const productCard = li.querySelector('article[data-product-id]') as HTMLElement | null;
      if (!productCard) {
        continue;
      }

      const priceAttr = productCard.getAttribute('data-lowest-available-price');
      const hasAvailableAttr = productCard.getAttribute('data-has-available');

      const priceCents = priceAttr ? parseInt(priceAttr, 10) : 0;
      const hasAvailable = hasAvailableAttr === '1';

      // Read the existing CSS order so we can re-map without disturbing banners
      const existingOrderRaw = li.style.getPropertyValue('--product-grid-order');
      const existingOrder = existingOrderRaw ? parseFloat(existingOrderRaw) : index + 1;

      productItems.push({
        li,
        priceCents: Number.isFinite(priceCents) ? priceCents : 0,
        hasAvailable,
        originalOrder: existingOrder,
        stableIndex: index,
      });
      index += 1;
    }

    if (productItems.length === 0) return;

    // Sort products by availability (available first) then by price
    const sorted = [...productItems].sort((a, b) => {
      // Unavailable products always go to the end
      if (a.hasAvailable !== b.hasAvailable) {
        return a.hasAvailable ? -1 : 1;
      }

      // Both available or both unavailable; compare prices
      if (isAsc) {
        if (a.priceCents !== b.priceCents) return a.priceCents - b.priceCents;
      } else if (isDesc) {
        if (a.priceCents !== b.priceCents) return b.priceCents - a.priceCents;
      }

      // Stable tiebreaker by original index
      return a.stableIndex - b.stableIndex;
    });

    // Preserve banner positions by reassigning only the product items' order numbers.
    // Collect and sort the existing product-only order numbers so banners keep theirs.
    const productOrders = productItems.map((p) => p.originalOrder).sort((a, b) => a - b);

    // Map sorted products onto the same set of order numbers
    sorted.forEach((item, i) => {
      const newOrder = productOrders[i] ?? i + 1;
      item.li.style.setProperty('--product-grid-order', String(newOrder));
    });
  }

  private setupResizeObserver() {
    const productList = this.querySelector('#product-list') as HTMLElement;
    if (!productList) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.checkGridAlignment();
    });

    this.resizeObserver.observe(productList);
  }

  public checkGridAlignment() {
    const productList = this.querySelector('#product-list') as HTMLElement;
    if (!productList) return;

    // Count all grid items (products and banners)
    const gridItems = productList.querySelectorAll(':scope > li');
    const itemCount = gridItems.length;

    // Get the container width
    const containerWidth = productList.clientWidth;

    // Calculate minimum card width and gap based on current breakpoint.
    // Matches grid-collection default (40px) and sparse grid (40px at ≥898px).
    let minCardWidth = 257; // Default desktop
    let gapSize = 40; // Default grid gap

    if (window.innerWidth <= 408) {
      minCardWidth = 120;
      gapSize = 16;
    } else if (window.innerWidth <= 586) {
      minCardWidth = 180;
      gapSize = 16;
    }

    // Calculate how many columns can fit
    // Formula: (containerWidth + gap) / (minCardWidth + gap)
    const maxColumns = Math.floor((containerWidth + gapSize) / (minCardWidth + gapSize));

    // If item count is less than max columns, apply the sparse grid modifier
    // This triggers CSS that uses fixed max-width columns aligned left
    if (itemCount > 0 && itemCount < maxColumns) {
      productList.setAttribute('data-sparse-grid', 'true');
    } else {
      productList.removeAttribute('data-sparse-grid');
    }
  }

  disconnectedCallback() {
    // Cleanup resize observer when element is removed
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

if (!customElements.get('product-grid')) {
  window.customElements.define('product-grid', ProductGrid);
}
